import { useTranslate } from '@refinedev/core';
import { BooleanField } from '@refinedev/antd';
import { Card, Descriptions, Flex, Space, Tag, Typography, theme } from 'antd';
import type { DescriptionsProps } from 'antd';
import type { OAuthClientOut, UserOut } from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { formatDateTime } from '@/utils/format';

const { Text } = Typography;

const TagList = ({ values, color }: { values?: string[]; color?: string }) =>
  orDash(
    values && values.length > 0 ? (
      <Space wrap size={[4, 4]}>
        {values.map((value) => (
          <Tag key={value} color={color} style={{ marginInlineEnd: 0 }}>
            {value}
          </Tag>
        ))}
      </Space>
    ) : null
  );

interface OAuthClientGeneralTabProps {
  record: OAuthClientOut;
  user?: UserOut;
}

export const OAuthClientGeneralTab = ({
  record,
  user,
}: OAuthClientGeneralTabProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const verticalDescProps = useVerticalDescProps();

  const infoItems: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: <Text code>{record.id}</Text>,
    },
    {
      key: 'client_id',
      label: t('iam.oauth_clients.fields.client_id'),
      children: (
        <Text code copyable>
          {record.client_id}
        </Text>
      ),
    },
    {
      key: 'client_name',
      label: t('iam.oauth_clients.fields.client_name'),
      children: orDash(record.client_name),
    },
    {
      key: 'client_secret',
      label: t('iam.oauth_clients.fields.client_secret'),
      children: orDash(
        record.client_secret ? (
          <Text code copyable ellipsis style={{ maxWidth: 240 }}>
            {record.client_secret}
          </Text>
        ) : null
      ),
    },
    {
      key: 'user',
      label: t('iam.oauth_clients.fields.user'),
      children: orDash(
        record.user_id != null ? (
          <ShowIconLink resource="iam.users" id={record.user_id}>
            {user?.email ?? user?.username ?? record.user_id}
          </ShowIconLink>
        ) : null
      ),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  const configItems: DescriptionsProps['items'] = [
    {
      key: 'is_confidential',
      label: t('iam.oauth_clients.fields.is_confidential'),
      children: <BooleanField value={record.is_confidential} />,
    },
    {
      key: 'require_pkce',
      label: t('iam.oauth_clients.fields.require_pkce'),
      children: <BooleanField value={record.require_pkce} />,
    },
    {
      key: 'scope',
      label: t('iam.oauth_clients.fields.scope'),
      children: orDash(record.scope),
    },
    {
      key: 'grant_types',
      label: t('iam.oauth_clients.fields.grant_types'),
      span: 3,
      children: <TagList values={record.grant_types} color="blue" />,
    },
    {
      key: 'response_types',
      label: t('iam.oauth_clients.fields.response_types'),
      span: 3,
      children: <TagList values={record.response_types} color="green" />,
    },
    {
      key: 'redirect_uris',
      label: t('iam.oauth_clients.fields.redirect_uris'),
      span: 3,
      children: <TagList values={record.redirect_uris} />,
    },
  ];

  return (
    <Flex vertical gap={token.margin}>
      <Card title={t('iam.oauth_clients.sections.client_info')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, md: 3 }}
          items={infoItems}
        />
      </Card>
      <Card title={t('iam.oauth_clients.sections.configuration')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, md: 3 }}
          items={configItems}
        />
      </Card>
    </Flex>
  );
};
